import Phaser from 'phaser';
import { accountClient, signedInRole } from '../../account/accountUi';
import { listCommandLog, markInboxSeen } from '../../account/adminInbox';
import { ROLE_NAME_COLORS, commandText, type CommandLogRow } from '../../config/devCommandLog';
import { drawStarfield } from '../starfield';
import { drawAvatarIcon } from '../avatarIcon';
import { installDebugReturn } from '../debugMode';
import { ScrollList } from '../../ui/scrollList';

const LIST_X = 110;
const LIST_W = 1040;
const LIST_TOP = 150;
const LIST_H = 520;
const AVATAR = 36;
const ROW_GAP = 10;

/**
 * AdminInboxScene — the admin's INBOX (spec 2026-10-24): every dev command
 * aimed at another player or at everyone, newest first, as "<name> has done
 * the command /give user34 bags 2". The name is coloured by role (lower dev
 * green, higher dev blue, admin gold) with the sender's leaderboard icon to
 * its right. The MESSAGES toggle at the top shows only /message commands.
 * Opening it marks everything seen, which clears the menu's red "!".
 *
 * Admins only: the RPCs refuse anyone else, and so does this screen.
 */
export class AdminInboxScene extends Phaser.Scene {
  private list?: ScrollList;
  private messagesOnly = false;
  private status?: Phaser.GameObjects.Text;
  private toggleBg?: Phaser.GameObjects.Graphics;
  private toggleText?: Phaser.GameObjects.Text;
  private loadSeq = 0;
  private gone = false;

  constructor() {
    super({ key: 'AdminInboxScene' });
  }

  create(): void {
    this.messagesOnly = false;
    this.gone = false;
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => (this.gone = true));
    drawStarfield(this, 1280, 720);
    this.add
      .text(640, 44, 'INBOX', { fontSize: '38px', color: '#f5c518', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setName('inbox-title');
    this.add
      .text(640, 80, 'Every dev command sent to another player or to everyone.', {
        fontSize: '15px',
        color: '#c8d2d8',
      })
      .setOrigin(0.5);

    const back = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setName('inbox-back')
      .setInteractive({ useHandCursor: true });
    back.on('pointerover', () => back.setColor('#f5c518'));
    back.on('pointerout', () => back.setColor('#cfe0bd'));
    back.on('pointerup', () => this.scene.start('MenuScene'));

    this.buildToggle();

    this.list = new ScrollList(this, {
      x: LIST_X,
      y: LIST_TOP,
      width: LIST_W,
      height: LIST_H,
      barX: LIST_X + LIST_W + 18,
      barWidth: 12,
      cullText: true,
    });
    this.status = this.add
      .text(640, LIST_TOP + 60, '', { fontSize: '20px', color: '#9aa6ad', fontStyle: 'italic' })
      .setOrigin(0.5)
      .setName('inbox-status');

    const client = accountClient();
    if (!client || signedInRole() !== 'admin') {
      this.status.setText('The inbox is for admins only.');
    } else {
      // Opening the inbox is what clears the "!".
      void markInboxSeen(client);
      void this.fetchRows();
    }

    installDebugReturn(this);
  }

  /** The MESSAGES pill: off shows every command, on only /message. */
  private buildToggle(): void {
    const x = 640;
    const y = 116;
    const w = 190;
    const h = 36;
    this.toggleBg = this.add.graphics();
    this.toggleText = this.add
      .text(x, y, '', { fontSize: '17px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5);
    const hit = this.add
      .zone(x, y, w, h)
      .setName('inbox-messages-toggle')
      .setInteractive({ useHandCursor: true });
    const draw = () => {
      const g = this.toggleBg!;
      g.clear();
      g.fillStyle(this.messagesOnly ? 0x2e9e4f : 0x2a2f3a, 1);
      g.fillRoundedRect(x - w / 2, y - h / 2, w, h, h / 2);
      g.lineStyle(2, 0xffffff, this.messagesOnly ? 0.9 : 0.4);
      g.strokeRoundedRect(x - w / 2, y - h / 2, w, h, h / 2);
      this.toggleText!.setText(this.messagesOnly ? '✓ MESSAGES' : 'MESSAGES');
    };
    draw();
    hit.on('pointerup', () => {
      this.messagesOnly = !this.messagesOnly;
      draw();
      void this.fetchRows();
    });
  }

  private async fetchRows(): Promise<void> {
    const client = accountClient();
    if (!client || !this.list) return;
    const seq = ++this.loadSeq;
    this.status?.setText('Loading…');
    const r = await listCommandLog(client, this.messagesOnly);
    // A toggle pressed mid-load wins; a scene that has gone takes nothing.
    if (seq !== this.loadSeq || this.gone) return;
    if (!r.ok) {
      this.status?.setText('Could not load the inbox — try again.');
      return;
    }
    this.status?.setText(
      r.value.length === 0 ? (this.messagesOnly ? 'No messages yet.' : 'No commands yet.') : '',
    );
    this.fill(r.value);
  }

  private fill(rows: CommandLogRow[]): void {
    const list = this.list!;
    list.content.removeAll(true);
    let y = 0;
    rows.forEach((row, i) => {
      const name = this.add
        .text(14, 0, row.senderUsername, {
          fontSize: '18px',
          color: ROLE_NAME_COLORS[row.senderRole],
          fontStyle: 'bold',
        })
        .setOrigin(0, 0)
        .setName(`inbox-row-${i}-name`);
      // The sender's leaderboard icon, to the right of their name.
      const iconX = name.x + name.width + 8 + AVATAR / 2;
      const restX = iconX + AVATAR / 2 + 8;
      const rest = this.add
        .text(restX, 0, `has done the command ${commandText(row)}`, {
          fontSize: '18px',
          color: '#e8ecef',
          // Clear of the timestamp in the row's top-right corner.
          wordWrap: { width: LIST_W - restX - 160, useAdvancedWrap: true },
        })
        .setOrigin(0, 0)
        .setName(`inbox-row-${i}-text`);
      const when = this.add
        .text(LIST_W - 12, 0, shortTime(row.createdAt), { fontSize: '13px', color: '#8a96a0' })
        .setOrigin(1, 0);
      const rowH = Math.max(AVATAR, rest.height) + 16;
      const plate = this.add
        .rectangle(LIST_W / 2, y + rowH / 2, LIST_W - 4, rowH, i % 2 ? 0x161a24 : 0x1c2130, 0.92)
        .setStrokeStyle(1, 0x2f3d4c, 1);
      name.setY(y + 8 + (AVATAR - name.height) / 2);
      rest.setY(y + 8 + (rest.height > AVATAR ? 0 : (AVATAR - rest.height) / 2));
      when.setY(y + 8);
      list.content.add([plate, name, rest, when]);
      const icon = row.senderAvatar ? drawAvatarIcon(this, row.senderAvatar, AVATAR, AVATAR) : null;
      if (icon) {
        icon.setPosition(iconX, y + 8 + AVATAR / 2);
        list.content.add(icon);
      } else {
        // No icon chosen: the empty circle the leaderboard draws.
        list.content.add(
          this.add.circle(iconX, y + 8 + AVATAR / 2, AVATAR / 2 - 2).setStrokeStyle(2, 0x4a5563, 1),
        );
      }
      y += rowH + ROW_GAP;
    });
    list.scrollToTop();
    list.setContentHeight(y);
  }
}

/** "Oct 24, 21:05" in the admin's own time zone. */
function shortTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
